import React from "react";
import { Form, Input, InputNumber, Select, DatePicker, Row, Col } from "antd";
import type { FormInstance } from "antd";
import type { Dayjs } from "dayjs";

import { VitalSignField } from "../../../../../types/healthcare/VitalSignTypes";

export interface VitalsFormValues {
	recorded_at: Dayjs | null;
	values: Record<string, number | string | null>;
}

interface VitalsFormProps {
	form: FormInstance<VitalsFormValues>;
	schema: VitalSignField[];
	initialValues: VitalsFormValues;
}

/**
 * Shared vitals/observations field set — reuses the RecordVitalSignsTaskForm entry
 * pattern (numeric → InputNumber with unit addon) but is driven entirely by the
 * `/vital-signs/schema` response so there is one source of truth for fields, units
 * and bounds. Coded fields render a Select when the schema supplies an options
 * domain, otherwise a free-text Input (heart_rhythm / temperature_site have
 * unknown domains → empty options → free text is correct).
 */
const VitalsForm: React.FC<VitalsFormProps> = ({ form, schema, initialValues }) => {
	return (
		<Form<VitalsFormValues> form={form} layout="vertical" initialValues={initialValues}>
			<Row gutter={[16, 0]}>
				<Col xs={24} md={12}>
					<Form.Item name="recorded_at" label="Recorded at">
						<DatePicker
							showTime
							format="DD MMM YYYY HH:mm"
							style={{ width: "100%" }}
							data-testid="vitals-form-recorded-at"
						/>
					</Form.Item>
				</Col>
			</Row>

			<Row gutter={[16, 0]}>
				{schema.map((field) => {
					const codedOptions = Object.entries(field.options || {}).map(([value, label]) => ({
						value,
						label,
					}));
					const label = field.unit ? `${field.label} (${field.unit})` : field.label;

					let control: React.ReactNode;
					if (field.type === "coded") {
						control =
							codedOptions.length > 0 ? (
								<Select
									allowClear
									placeholder="Select"
									options={codedOptions}
									data-testid={`vitals-form-${field.key}`}
								/>
							) : (
								<Input allowClear placeholder="Enter value" data-testid={`vitals-form-${field.key}`} />
							);
					} else {
						control = (
							<InputNumber
								style={{ width: "100%" }}
								min={field.min ?? undefined}
								max={field.max ?? undefined}
								addonAfter={field.unit ?? undefined}
								placeholder="Enter value"
								data-testid={`vitals-form-${field.key}`}
							/>
						);
					}

					return (
						<Col xs={24} md={12} key={field.key}>
							<Form.Item name={["values", field.key]} label={label}>
								{control}
							</Form.Item>
						</Col>
					);
				})}
			</Row>
		</Form>
	);
};

export default VitalsForm;
